'use client';

import { useUserData } from '@/context/auth/user-data-context';
import { LanguageIsoCode } from '@/graphql/graphql';
import { fetchCategoriesList } from '@/lib/api-categories';
import { CategoryDropdownMenu } from '@/types/categories';
import { useLocale, useTranslations } from 'next-intl';
import { useSelectedLayoutSegments } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import BuyerSellerDropDown from './buyer-seller-dropdown';
import CategoriesDropDown from './catagories-dropdown';
import FormatsDropDown from './formats-dropdown';
import NavigationLink from './navigation-link';

type Props = {
    isMobile?: boolean;
    onLinkClickAction?: () => void;
};

type MatchMode = 'exact' | 'inclusive';

function segmentsFromPath(href: string) {
    return href
        .split('/')
        .filter(Boolean)
        .map((s) => s.toLowerCase());
}

export default function Navigation({ isMobile = false, onLinkClickAction }: Props) {
    const t = useTranslations('Navigation');
    const { isBuyer, isSeller } = useUserData();
    const locale = useLocale().toLowerCase();

    const [categories, setCategories] = useState<CategoryDropdownMenu[]>([]);
    const [catLoaded, setCategoriesLoaded] = useState<boolean>(false);
    const [catMenuOpen, setCatMenuOpen] = useState<boolean>(false);

    const segments = useSelectedLayoutSegments();
    const currentSegments = Array.isArray(segments) ? segments.filter(Boolean).map((s) => s.toLowerCase()) : [];

    function isLinkActive(href: string, matchMode: MatchMode = 'exact'): boolean {
        const hrefPath = typeof href === 'string' ? href : (href ?? '/');
        const hrefSegments = segmentsFromPath(hrefPath);

        const isExact = (() => {
            if (hrefSegments.length !== currentSegments.length) return false;
            for (let i = 0; i < hrefSegments.length; i++) {
                if (hrefSegments[i] !== currentSegments[i]) return false;
            }
            return true;
        })();

        const isInclusive = (() => {
            if (hrefSegments.length === 0 && currentSegments.length === 0) return true;
            if (hrefSegments.length === 0) return false;
            if (hrefSegments.length > currentSegments.length) return false;
            for (let i = 0; i < hrefSegments.length; i++) {
                if (hrefSegments[i] !== currentSegments[i]) return false;
            }
            return true;
        })();

        return matchMode === 'exact' ? isExact : isInclusive;
    }

    useEffect(() => {
        if (!catLoaded) {
            const fetchCats = async () => {
                const categoriesRes = await fetchCategoriesList({ language: locale.toUpperCase() as LanguageIsoCode });
                const categoriesHardcoded = process.env.NEXT_PUBLIC_GENRE_CATALOG_MENU_IDS
                    ? JSON.parse(process.env.NEXT_PUBLIC_GENRE_CATALOG_MENU_IDS)
                    : {};
                const formattedCategories = [
                    ...categoriesRes.data,
                    ...Object.keys(categoriesHardcoded).map((key) => {
                        const genreKey =
                            `genreCatalogMenuItems.${categoriesHardcoded[key].toLowerCase()}.title` as Parameters<
                                typeof t
                            >[0];
                        return {
                            identifier: { id: key },
                            titles: {
                                [locale.toLowerCase() as LanguageIsoCode]: t(genreKey),
                            },
                            isAnExcludedGenre: true,
                        };
                    }),
                ].sort((a, b) => {
                    const titleA = a.titles[locale.toLowerCase()] || '';
                    const titleB = b.titles[locale.toLowerCase()] || '';
                    return titleA.localeCompare(titleB);
                });
                setCategories(formattedCategories);
                setCategoriesLoaded(true);
            };

            fetchCats();
        }
    }, [locale, catLoaded]);

    const containerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                (event.target instanceof Element && event.target.matches('a')) ||
                (containerRef.current && event.target instanceof Node && !containerRef.current.contains(event.target))
            ) {
                setCatMenuOpen(false);
            }
        };

        if (catMenuOpen) document.addEventListener('click', handleClickOutside);

        return () => document.removeEventListener('click', handleClickOutside);
    }, [catMenuOpen]);

    const showGuestMenu = !isSeller && !isBuyer;

    return (
        <nav
            className={`text-white transition-all duration-300 ${
                isMobile ? 'flex flex-col gap-2' : 'flex items-center space-x-6'
            }`}
        >
            {showGuestMenu ? (
                <BuyerSellerDropDown
                    isActive={isLinkActive('/tv-rights-buyers') || isLinkActive('/tv-rights-sellers ')}
                />
            ) : null}
            {isSeller ? (
                <NavigationLink
                    href="/programs/dashboard"
                    isActive={isLinkActive('/programs/dashboard', 'inclusive')}
                    onClick={onLinkClickAction}
                >
                    {t('dashboard')}
                </NavigationLink>
            ) : null}
            {isSeller || isBuyer || showGuestMenu ? (
                <CategoriesDropDown
                    isActive={isLinkActive('/programs')}
                    categories={categories}
                    onLinkClickAction={onLinkClickAction}
                />
            ) : undefined}
            {isSeller || isBuyer || showGuestMenu ? (
                <FormatsDropDown isActive={isLinkActive('/formats', 'inclusive')} />
            ) : null}
            {isBuyer ? (
                <NavigationLink href="/licenses" isActive={isLinkActive('/licenses')} onClick={onLinkClickAction}>
                    {t('licences')}
                </NavigationLink>
            ) : null}
        </nav>
    );
}
